import type {
  AgentActions,
  AgentApiMessage,
  AgentKeySource,
  AgentProviderId,
  AgentToolCall,
  ChatMessage,
} from './types'
import {
  agentProviderLabel,
  DEFAULT_AGENT_MODEL,
  normalizeCompletionResponse,
  normalizeProvider,
  requestAgentCompletion,
} from './api'
import { buildAgentSystemContent, MAX_AGENT_TOOL_ROUNDS } from './prompt'
import {
  agentToolsForTurn,
  executeAgentTool,
  formatToolCallsAsPlan,
  recoverToolCallsFromText,
  stripNativeToolMarkup,
  textHasUnexecutedToolIntent,
} from './tools'
import { hasPlanStage, latestEvaluateVerdict, surfaceThinkingFromTurn, visibleAssistantContent } from './workflow'

/**
 * Groq reasoning models (especially Qwen 3.6) spend completion tokens on
 * the thinking trace *before* emitting tool_calls or a reply. Groq's
 * default cap is 1024, which is easy to exhaust mid-thought — the API
 * then returns HTTP 200 with empty `content` and no `tool_calls`.
 */
export const GROQ_MAX_COMPLETION_TOKENS = 8192

export class AgentTurnAborted extends Error {
  constructor(message = "Stopped.") {
    super(message)
    this.name = "AgentTurnAborted"
  }
}

export function throwIfAgentAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new AgentTurnAborted()
}

export function isAgentTurnAborted(err: unknown): boolean {
  if (err instanceof AgentTurnAborted) return true
  return err instanceof Error && err.name === "AbortError"
}

function liveGraphSnapshot(
  actions: AgentActions | undefined,
  fallback: string
): string {
  try {
    const snapshot = actions?.getReadableModel?.()
    if (typeof snapshot === "string") return snapshot
  } catch {
    // Fall through to the turn-start snapshot.
  }
  return fallback
}

/** Pin evaluate/follow-up rounds to the canvas as it is AFTER tools ran. */
function attachRefreshedGraphToLatestToolResult(
  apiMessages: AgentApiMessage[],
  snapshot: string
): void {
  for (let i = apiMessages.length - 1; i >= 0; i--) {
    if (apiMessages[i].role !== "tool") continue
    const content = apiMessages[i].content ?? ""
    apiMessages[i].content =
      content +
      "\n\n=== Refreshed model graph (after this tool batch) ===\n" +
      (snapshot.trim() || "(graph is empty)")
    return
  }
}

function groqRequestExtras(provider: AgentProviderId): Record<string, unknown> {
  if (provider !== "groq") return {}
  return {
    max_completion_tokens: GROQ_MAX_COMPLETION_TOKENS,
    // Split Qwen3's <think>…</think> block into message.reasoning.
    reasoning_format: "parsed",
  }
}

/** Groq's gpt-oss family rejects / ignores parallel tool use. */
function modelSupportsParallelToolCalls(model: string): boolean {
  return !/gpt-oss/i.test(model)
}

export const PUBLIC_AEGIS_INTRO =
  "Hi! I can walk you through how this map was produced and what each result shows. Ask me about the process or a layer."

export const INITIAL_CHAT_MESSAGES: ChatMessage[] = openingChatMessages(false)

export function openingChatMessages(publicView: boolean, intro?: string): ChatMessage[] {
  const customIntro = intro?.trim()
  return [
    {
      id: "m1",
      role: "assistant",
      text: publicView
        ? (customIntro || PUBLIC_AEGIS_INTRO)
        : "Hi! I'm the model agent. I can help you reason about nodes, suggest connections, or summarise outputs. What are you working on?",
      timestamp: "now",
    },
  ]
}

/** Short HH:MM stamp for chat bubbles. */
export function chatTimestamp(): string {
  const now = new Date()
  const hh = String(now.getHours()).padStart(2, "0")
  const mm = String(now.getMinutes()).padStart(2, "0")
  return `${hh}:${mm}`
}

export function randomMessageId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Convert the visible transcript into OpenAI-shaped messages for the next
 * API call. Thinking bubbles are folded onto the following assistant turn
 * as `reasoning_content` so DeepSeek thinking mode can replay CoT when
 * `tools` are present.
 */
export function historyToApiMessages(history: ChatMessage[]): AgentApiMessage[] {
  const out: AgentApiMessage[] = []
  let pendingReasoning: string[] = []

  for (const message of history) {
    if (message.role === "thinking") {
      if (message.text.trim()) pendingReasoning.push(message.text)
      continue
    }
    if (message.role === "tool" || message.role === "plan") continue

    if (message.role === "assistant") {
      const reasoning = pendingReasoning.join("\n\n")
      pendingReasoning = []
      const apiMessage: AgentApiMessage = {
        role: "assistant",
        content: message.text,
      }
      if (reasoning) apiMessage.reasoning_content = reasoning
      out.push(apiMessage)
      continue
    }

    pendingReasoning = []
    out.push({
      role: "user",
      content: message.text,
    })
  }

  return out
}

function assistantReasoningContent(message: any): string | undefined {
  if (typeof message?.reasoning_content === "string" && message.reasoning_content.trim()) {
    return message.reasoning_content
  }
  if (typeof message?.reasoning === "string" && message.reasoning.trim()) {
    return message.reasoning
  }
  return undefined
}

function pushAssistantApiMessage(
  apiMessages: AgentApiMessage[],
  opts: {
    content: string
    toolCalls?: AgentToolCall[]
    reasoningContent?: string
  }
) {
  const message: AgentApiMessage = {
    role: "assistant",
    content: opts.content,
  }
  if (opts.toolCalls && opts.toolCalls.length > 0) {
    message.tool_calls = opts.toolCalls
  }
  if (opts.reasoningContent) {
    message.reasoning_content = opts.reasoningContent
  }
  apiMessages.push(message)
}

async function requestAgentWrapUpSummary(
  teamId: number | undefined,
  provider: AgentProviderId,
  model: string,
  apiMessages: AgentApiMessage[],
  onReasoning?: (text: string) => void,
  keySource?: AgentKeySource,
  signal?: AbortSignal,
  shareCode?: string
): Promise<string> {
  throwIfAgentAborted(signal)
  apiMessages.push({
    role: "user",
    content:
      "You have used the maximum number of tool calls allowed for this turn. " +
      "Do not call any more tools. Reply with a brief summary of what you " +
      "changed and what (if anything) is still incomplete.",
  })

  const response = await requestAgentCompletion(teamId, provider, {
    model,
    messages: apiMessages,
    temperature: 0.4,
    ...groqRequestExtras(provider),
  }, keySource, signal, shareCode)

  if (!response.ok) {
    return (
      "Made several graph edits this turn but hit the tool-call limit before " +
      "finishing. Check the tool events above for what completed."
    )
  }

  const data = response.data
  const message = data?.choices?.[0]?.message
  const reasoningText = typeof message?.reasoning === "string" ? message.reasoning : ""
  const contentText = typeof message?.content === "string" ? message.content : ""
  const cleanedContent = stripNativeToolMarkup(contentText)
  surfaceThinkingFromTurn(reasoningText, cleanedContent, onReasoning)
  const reply = visibleAssistantContent(cleanedContent)
  return (
    reply ||
    "Made several graph edits this turn but hit the tool-call limit before finishing."
  )
}

/**
 * Run one user turn against Groq with tool calling enabled, looping until
 * the model produces a plain text reply (or hits the safety ceiling). The
 * `actions` callbacks are invoked for each requested tool call; their
 * results are fed back to the model so it can iterate.
 *
 * `history` is the visible transcript (user + assistant turns) including
 * the new user message. `readableModel` is a fresh snapshot of the current
 * graph (nodes, IDs, connections, control values) and `readableComponents`
 * is the catalogue of every component the agent can ADD — both are
 * appended to the system prompt so the model can reason about the user's
 * actual canvas instead of guessing.
 *
 * `onToolEvent` is fired once per executed tool call with a short
 * human-readable label (e.g. "Added Numeric constant (#42)") so the chat
 * UI can surface activity in-line as it happens.
 *
 * Returns the final assistant text. Throws on transport / API errors.
 */
export async function runAgentTurn(opts: {
  /** Team whose server-side proxy (and stored API key) handles the request. */
  teamId: number | undefined
  /**
   * Which LLM provider to route through ("groq" | "anthropic" | "deepseek"). Defaults to
   * Groq. Selects both the proxy endpoint and the request/response translation.
   */
  provider?: string
  /**
   * Provider-specific model id (e.g. "qwen/qwen3-32b" or
   * "claude-sonnet-4-6"). Defaults to the built-in Groq model.
   */
  model?: string
  /** Which stored key to bill the request against. */
  keySource?: AgentKeySource
  /** Public share whose stored key and model handle the request. */
  shareCode?: string
  history: ChatMessage[]
  actions: AgentActions | undefined
  readableModel: string
  readableComponents: string
  /** Extra guidance from a public project's share settings. Omitted on the regular editor. */
  extraInstructions?: string
  /** Public /share view: process language, links only to map outputs. */
  publicView?: boolean
  onToolEvent?: (label: string) => void
  /**
   * Debug hook fired once per round with the batched tool-call plan (in the
   * `x1 = add_component(…)` notation) just before the calls execute, so the
   * UI can surface what the model intends to do.
   */
  onPlan?: (plan: string) => void
  /**
   * Emitted once per round whenever the model produces a reasoning trace
   * (Qwen3 `<think>` block, surfaced as `message.reasoning` by Groq when
   * `reasoning_format` is set to "parsed"). The text is the full thinking
   * for that round; the chat UI renders it inside a collapsible panel.
   */
  onReasoning?: (text: string) => void
  /** Abort in-flight provider requests and skip remaining tools / rounds. */
  signal?: AbortSignal
}): Promise<string> {
  const { teamId, history, actions, readableModel, readableComponents, extraInstructions, publicView, onToolEvent, onReasoning, onPlan, keySource, shareCode, signal } = opts
  const provider = normalizeProvider(opts.provider)
  const model = opts.model || DEFAULT_AGENT_MODEL
  // Symbolic-ref → real-id bindings for this turn. `add_component` writes to
  // it (when the model supplies a `ref`); the id-bearing tools read from it.
  // This is what lets the model batch a whole plan — create + wire + name —
  // in a single response instead of one round-trip per node.
  const refMap = new Map<string, number>()

  if (teamId === undefined || teamId === null) {
    throw new Error(
      "No team context available, so the model agent can't reach its API key. " +
      "Open the agent from within a team project."
    )
  }

  const apiMessages: AgentApiMessage[] = [
    {
      role: "system",
      content: buildAgentSystemContent({ readableModel, readableComponents, actions, extraInstructions, publicView }),
    },
    ...historyToApiMessages(history),
  ]

  // Groq returns HTTP 400 with `error.code === "tool_use_failed"` when the
  // model produces a malformed tool_calls payload (bad JSON args, wrong
  // tool name, etc.). Qwen 3.6 also frequently returns HTTP 200 with
  // empty content / empty tool_calls after a long reasoning trace. We
  // forgive those once or twice per turn by recovering parseable calls
  // from the raw text or nudging the model to emit structured tool_calls.
  let recoveryAttempts = 0
  const MAX_RECOVERY_ATTEMPTS = 2
  let toolsRanThisTurn = false

  const executeToolBatch = async (
    toolCalls: AgentToolCall[],
    assistantContent: string,
    reasoningContent?: string
  ) => {
    throwIfAgentAborted(signal)
    onPlan?.(formatToolCallsAsPlan(toolCalls))
    pushAssistantApiMessage(apiMessages, {
      content: assistantContent,
      toolCalls,
      reasoningContent,
    })
    for (const call of toolCalls) {
      throwIfAgentAborted(signal)
      const result = await executeAgentTool(call, actions, refMap)
      if (result.label) onToolEvent?.(result.label)
      apiMessages.push({
        role: "tool",
        tool_call_id: call.id,
        name: call.function?.name,
        content: result.content,
      })
    }
    // Re-read the live editor AFTER this batch so the next round's
    // <evaluate> (and the system snapshot) see new nodes / wires, not the
    // pre-turn graph the model planned against.
    const snapshot = liveGraphSnapshot(actions, readableModel)
    apiMessages[0].content = buildAgentSystemContent({
      readableModel: snapshot,
      readableComponents,
      actions,
      extraInstructions,
      publicView,
    })
    attachRefreshedGraphToLatestToolResult(apiMessages, snapshot)
  }

  for (let round = 0; round < MAX_AGENT_TOOL_ROUNDS; round++) {
    throwIfAgentAborted(signal)
    // Keep the graph snapshot in the system message current — without this
    // the model keeps seeing the pre-turn canvas while tool results carry
    // new ids, which leads to wrong connects and endless retries.
    apiMessages[0].content = buildAgentSystemContent({
      readableModel,
      readableComponents,
      actions,
      extraInstructions,
      publicView,
    })

    const response = await requestAgentCompletion(teamId, provider, {
      model,
      messages: apiMessages,
      tools: agentToolsForTurn(publicView === true, actions),
      tool_choice: "auto",
      // Allow batched (parallel) tool calls so the model can emit a whole
      // plan — add + add + connect + name — in ONE response. It binds each
      // new node to a symbolic `ref` (x1, x2…) and references those refs in
      // the connect/name calls; we resolve refs → real ids in call order as
      // we execute the batch (see refMap / resolveNodeId). This collapses
      // what used to be one round-trip per node into ~2 LLM calls per turn.
      // Previously this was forced false because the model couldn't predict
      // ids and would guess — refs remove that failure mode.
      parallel_tool_calls: modelSupportsParallelToolCalls(model),
      temperature: 0.4,
      ...groqRequestExtras(provider),
    }, keySource, signal, shareCode)

    if (!response.ok) {
      const errBody = response.data
      const errCode = errBody?.error?.code
      const failedGeneration = errBody?.error?.failed_generation
      const detail =
        errBody?.error?.message ??
        (errBody ? JSON.stringify(errBody) : "")

      if (
        response.status === 400 &&
        errCode === "tool_use_failed" &&
        typeof failedGeneration === "string"
      ) {
        // Llama-3.x sometimes emits its *native* tool-call syntax
        // (`<function=name{...args...}</function>`) instead of Groq's
        // OpenAI-style tool_calls payload. Groq rejects that with a 400,
        // but the model's *intent* is perfectly readable — so we parse
        // the failed generation and run the recovered call ourselves
        // rather than burning the recovery budget on a "try again" prompt.
        const recovered = recoverToolCallsFromText(failedGeneration)
        if (recovered.length > 0) {
          await executeToolBatch(recovered, "")
          continue
        }

        if (recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
          recoveryAttempts++
          const excerpt = failedGeneration.slice(0, 600)
          apiMessages.push({
            role: "user",
            content:
              "Your previous reply was rejected because the tool_calls payload " +
              "was malformed (invalid JSON arguments or an unknown tool name). " +
              "Please reissue the tool call with arguments that match the " +
              "schema exactly, or reply with plain text if no tool is needed. " +
              `Groq's failed_generation excerpt:\n${excerpt}`,
          })
          round--
          continue
        }
      }

      const failedExcerpt =
        typeof failedGeneration === "string" && failedGeneration.trim()
          ? ` Failed generation: ${failedGeneration.slice(0, 200)}…`
          : ""
      const providerLabel = agentProviderLabel(provider)
      throw new Error(
        `${providerLabel} request failed (${response.status} ${response.statusText})${detail ? `: ${detail}` : ""}${failedExcerpt}`
      )
    }

    const data = response.data
    const choice = data?.choices?.[0]
    const message = choice?.message
    const finishReason = typeof choice?.finish_reason === "string" ? choice.finish_reason : ""
    const reasoningText = typeof message?.reasoning === "string" ? message.reasoning : ""
    const reasoningContent = assistantReasoningContent(message)
    const contentText = typeof message?.content === "string" ? message.content : ""
    const structuredCalls: AgentToolCall[] = Array.isArray(message?.tool_calls)
      ? message.tool_calls
      : []

    // Qwen 3.6 on Groq often parks the actual call in `reasoning` or as
    // XML in `content` and leaves `tool_calls` empty. Recover those so we
    // execute the plan instead of treating the turn as a blank reply.
    const recoveredCalls =
      structuredCalls.length > 0
        ? []
        : recoverToolCallsFromText([contentText, reasoningText].filter(Boolean).join("\n\n"))
    const toolCalls = structuredCalls.length > 0 ? structuredCalls : recoveredCalls

    const cleanedContent = stripNativeToolMarkup(contentText)

    // Models often emit <reason>/<plan>/<evaluate> in message.content (not
    // message.reasoning). Route tagged text to the workflow UI and strip it
    // from the visible reply / API transcript.
    surfaceThinkingFromTurn(reasoningText, cleanedContent, onReasoning)
    const visibleContent = visibleAssistantContent(cleanedContent)
    const evaluateVerdict = latestEvaluateVerdict(reasoningText, cleanedContent)
    const passedEval = evaluateVerdict === "pass"

    if (toolCalls.length > 0) {
      // After tools have already run this turn, a PASS evaluate means the
      // graph matches the plan. Extra tool_calls (or leftovers recovered
      // from the echoed <plan>) would start a duplicate loop.
      if (passedEval && toolsRanThisTurn) {
        return visibleContent || "The graph matches the plan."
      }
      // Recovered dumps often come with a premature "done" summary — don't
      // echo that into the transcript or the model will skip a real evaluate.
      await executeToolBatch(
        toolCalls,
        structuredCalls.length > 0 ? visibleContent : "",
        reasoningContent
      )
      toolsRanThisTurn = true
      continue
    }

    const reply = visibleContent
    const rawForIntent = [contentText, reasoningText].filter(Boolean).join("\n\n")
    // A PASS evaluate still contains earlier <reason>/<plan> tags in the
    // same thinking blob — that is finished work, not an unexecuted plan.
    // Only recover when there is no passing eval, or tools never ran and
    // the model dumped calls as text (premature PASS / silent plan).
    const dumpedUnexecutedTools = textHasUnexecutedToolIntent(rawForIntent)
    if (passedEval && (toolsRanThisTurn || !dumpedUnexecutedTools)) {
      return reply || "The graph matches the plan."
    }
    const plannedButSilent =
      finishReason === "length" ||
      evaluateVerdict === "fail" ||
      hasPlanStage(rawForIntent) ||
      dumpedUnexecutedTools

    if (!reply) {
      if (plannedButSilent && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
        recoveryAttempts++
        const remembered = [cleanedContent, reasoningText.trim()].filter(Boolean).join("\n\n")
        if (remembered) {
          pushAssistantApiMessage(apiMessages, {
            content: remembered.slice(0, 4000),
            reasoningContent,
          })
        }
        apiMessages.push({
          role: "user",
          content:
            finishReason === "length"
              ? "Your previous reply was cut off before you could emit tool_calls or a final answer. " +
                "Continue from where you left off. If you planned graph edits, emit them now as " +
                "structured tool_calls — do not describe the calls in prose or XML."
              : evaluateVerdict === "fail"
                ? "Your <evaluate> concluded FAIL but you did not emit tool_calls to fix the " +
                  "discrepancies. Emit those fixes now as structured tool_calls."
              : "You finished reasoning (or wrote a <plan>) but did not emit any tool_calls and " +
                "produced no user-facing reply. If the graph should change, emit that plan now " +
                "using the structured tool_calls field. If no tools are needed, reply with a " +
                "brief summary or a <question> block. Never return an empty message.",
        })
        round--
        continue
      }
      throw new Error(
        `${agentProviderLabel(provider)} returned an empty response.`
      )
    }

    // Premature PASS (or a plan / FAIL) with a tool-call dump in text and
    // no API tool_calls — recover so the edits actually run. Do not do
    // this after a real PASS following executed tools.
    if (plannedButSilent && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
      recoveryAttempts++
      pushAssistantApiMessage(apiMessages, {
        content: (reply || cleanedContent || reasoningText).slice(0, 4000),
        reasoningContent,
      })
      apiMessages.push({
        role: "user",
        content:
          evaluateVerdict === "fail"
            ? "Your <evaluate> concluded FAIL. Emit the fix now as structured tool_calls. " +
              "Do not write another summary until those tools have run."
            : "You described graph edits (a <plan> and/or a tool_calls JSON dump) but did not " +
              "emit them via the API tool_calls field, so nothing was executed. Emit those " +
              "calls now as structured tool_calls. Do not write a summary until the tools have run.",
      })
      round--
      continue
    }

    return reply
  }

  return requestAgentWrapUpSummary(teamId, provider, model, apiMessages, onReasoning, keySource, signal, shareCode)
}

